import React, { useState } from "react";
import useReportFormat from "../useReportFormat";

const RemainingCell = ({ value, size = "0.88rem", formatCurrency }) => {
  if (value === 0) {
    return <span style={{ fontWeight: 700, color: "#64748b", fontSize: size }}>—</span>;
  }
  const over = value < 0;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
        fontWeight: 800,
        color: over ? "#dc2626" : "#059669",
        fontSize: size,
      }}
    >
      {over && <i className="ri-alert-line" style={{ fontSize: "0.8rem" }} />}
      {formatCurrency(value)}
    </span>
  );
};

const colStyle = {
  padding: "14px 20px",
  textAlign: "right",
  whiteSpace: "nowrap",
};
const colStyleLeft = {
  padding: "14px 20px",
  textAlign: "left",
};

const thStyle = {
  padding: "12px 20px",
  fontSize: "0.68rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color: "#64748b",
  background: "#f8fafc",
  borderBottom: "1px solid #e2e8f0",
  whiteSpace: "nowrap",
};

const CostHierarchyTable = ({ groups }) => {
  const formatCurrency = useReportFormat();
  const data = groups && groups.length > 0 ? groups : [];
  const [expanded, setExpanded] = useState({});

  const allExpanded = data.every((g) => expanded[g.id]);

  const toggleGroup = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleAll = () => {
    if (allExpanded) {
      setExpanded({});
    } else {
      const next = {};
      data.forEach((g) => {
        next[g.id] = true;
      });
      setExpanded(next);
    }
  };

  const grandBudget = data.reduce((s, g) => s + g.budget, 0);
  const grandSpent = data.reduce((s, g) => s + g.spent, 0);
  const grandCommitted = data.reduce((s, g) => s + (g.committed ?? 0), 0);
  const grandRemaining = grandBudget - grandSpent - grandCommitted;

  return (
    <div
      style={{
        background: "white",
        borderRadius: 16,
        marginBottom: 24,
        border: "1px solid #f0f4ff",
        overflow: "hidden",
        boxShadow: "0 4px 20px -4px rgba(0,0,0,0.05)",
      }}
    >
      {/* Header bar */}
      <div
        style={{
          background: "#f8fafc",
          padding: "18px 32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #e2e8f0",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <i
            className="ri-wallet-3-line"
            style={{ color: "#002045", fontSize: "1.1rem" }}
          />
          <span
            style={{ fontWeight: 700, fontSize: "0.95rem", color: "#002045" }}
          >
            Cost Hierarchy &amp; Budget Tracking
          </span>
        </div>
        <button
          onClick={toggleAll}
          style={{
            background: "white",
            border: "1px solid #e2e8f0",
            borderRadius: 6,
            padding: "6px 14px",
            fontSize: "0.78rem",
            fontWeight: 600,
            color: "#002045",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <i className={allExpanded ? "ri-contract-up-down-line" : "ri-expand-up-down-line"} />
          {allExpanded ? "Collapse All" : "Expand All"}
        </button>
      </div>

      {/* Table */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, textAlign: "left", minWidth: 260 }}>
                Cost Category
              </th>
              <th style={{ ...thStyle, textAlign: "right" }}>Budgeted</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Spent</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Committed</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Remaining</th>
            </tr>
          </thead>
          <tbody>
            {data.map((group) => {
              const isOpen = !!expanded[group.id];
              return (
                <React.Fragment key={group.id}>
                  {/* Group row */}
                  <tr
                    onClick={() => toggleGroup(group.id)}
                    style={{
                      background: "rgba(0,32,69,0.02)",
                      cursor: "pointer",
                      borderBottom: "1px solid #f0f4ff",
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "rgba(0,32,69,0.05)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "rgba(0,32,69,0.02)")
                    }
                  >
                    <td style={colStyleLeft}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <i
                          className="ri-arrow-right-s-line"
                          style={{
                            fontSize: "1rem",
                            color: "#002045",
                            transition: "transform 0.2s",
                            transform: isOpen
                              ? "rotate(90deg)"
                              : "rotate(0deg)",
                            flexShrink: 0,
                          }}
                        />
                        <span
                          style={{
                            fontWeight: 700,
                            color: "#002045",
                            fontSize: "0.88rem",
                          }}
                        >
                          {group.name}
                        </span>
                        {group.categories.length > 0 && (
                          <span
                            style={{
                              fontSize: "0.65rem",
                              fontWeight: 700,
                              background: "#e2e8f0",
                              color: "#64748b",
                              borderRadius: 99,
                              padding: "1px 7px",
                            }}
                          >
                            {group.categories.length}
                          </span>
                        )}
                      </div>
                    </td>
                    <td
                      style={{
                        ...colStyle,
                        fontWeight: 800,
                        color: "#002045",
                        fontSize: "0.88rem",
                      }}
                    >
                      {formatCurrency(group.budget)}
                    </td>
                    <td
                      style={{
                        ...colStyle,
                        fontWeight: 800,
                        color: "#002045",
                        fontSize: "0.88rem",
                      }}
                    >
                      {formatCurrency(group.spent)}
                    </td>
                    <td
                      style={{
                        ...colStyle,
                        fontWeight: 800,
                        color: "#d97706",
                        fontSize: "0.88rem",
                      }}
                    >
                      {formatCurrency(group.committed ?? 0)}
                    </td>
                    <td style={{ ...colStyle, fontWeight: 800, fontSize: "0.88rem" }}>
                      <RemainingCell value={group.budget - group.spent - (group.committed ?? 0)} formatCurrency={formatCurrency} />
                    </td>
                  </tr>

                  {/* Sub-rows */}
                  {isOpen &&
                    group.categories.map((cat, ci) => (
                      <tr
                        key={ci}
                        style={{
                          borderBottom: "1px solid #f8fafc",
                          background: "white",
                        }}
                      >
                        <td
                          style={{
                            ...colStyleLeft,
                            paddingLeft: 56,
                          }}
                        >
                          <span
                            style={{
                              fontSize: "0.84rem",
                              color: "#475569",
                              fontWeight: 500,
                            }}
                          >
                            {cat.name}
                          </span>
                        </td>
                        <td
                          style={{
                            ...colStyle,
                            color: "#475569",
                            fontSize: "0.84rem",
                            fontWeight: 500,
                          }}
                        >
                          {formatCurrency(cat.budget)}
                        </td>
                        <td
                          style={{
                            ...colStyle,
                            color: "#475569",
                            fontSize: "0.84rem",
                            fontWeight: 500,
                          }}
                        >
                          {formatCurrency(cat.spent)}
                        </td>
                        <td
                          style={{
                            ...colStyle,
                            color: "#d97706",
                            fontSize: "0.84rem",
                            fontWeight: 500,
                          }}
                        >
                          {formatCurrency(cat.committed ?? 0)}
                        </td>
                        <td style={{ ...colStyle, fontSize: "0.84rem" }}>
                          <RemainingCell value={cat.budget - cat.spent - (cat.committed ?? 0)} size="0.84rem" formatCurrency={formatCurrency} />
                        </td>
                      </tr>
                    ))}
                </React.Fragment>
              );
            })}

            {/* Grand Total row */}
            <tr style={{ background: "#f8fafc", borderTop: "2px solid #e2e8f0" }}>
              <td
                style={{
                  ...colStyleLeft,
                  fontWeight: 800,
                  color: "#002045",
                  fontSize: "0.88rem",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <i
                    className="ri-bar-chart-grouped-line"
                    style={{ color: "#002045", fontSize: "0.9rem" }}
                  />
                  Grand Total
                </div>
              </td>
              <td
                style={{
                  ...colStyle,
                  fontWeight: 800,
                  color: "#002045",
                  fontSize: "0.88rem",
                }}
              >
                {formatCurrency(grandBudget)}
              </td>
              <td
                style={{
                  ...colStyle,
                  fontWeight: 800,
                  color: "#002045",
                  fontSize: "0.88rem",
                }}
              >
                {formatCurrency(grandSpent)}
              </td>
              <td
                style={{
                  ...colStyle,
                  fontWeight: 800,
                  color: "#d97706",
                  fontSize: "0.88rem",
                }}
              >
                {formatCurrency(grandCommitted)}
              </td>
              <td style={{ ...colStyle, fontWeight: 800, fontSize: "0.88rem" }}>
                <RemainingCell value={grandRemaining} formatCurrency={formatCurrency} />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CostHierarchyTable;
